import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LlmModelConfig, LlmProviderConfig, LlmProviderKind, LlmProviderView, LlmSettingsView } from '@solar/shared';
import { SECRET_MASK } from '@solar/shared';
import { api } from '../../lib/api';
import { useT, type Dict } from '../../lib/i18n';
import { useSolar } from '../../lib/store';
import { PlusIcon, RefreshIcon, TrashIcon } from '../Icons';
import { rich } from './richText';

type PresetId = 'omniroute' | 'openai' | 'openrouter' | 'anthropic' | 'gemini' | 'ollama';

/**
 * Starting points; the base URL of self-hosted gateways must be adjusted. The label is the product name; a preset's
 * hint and a name with words to translate are in `t.settings.models.presetHint` / `presetName` under its id.
 */
const PRESETS: Array<{ id: PresetId; label: string; name: string; kind: LlmProviderKind; baseUrl: string; apiKey: string }> = [
  { id: 'omniroute', label: 'OmniRoute', name: 'OmniRoute', kind: 'openai-chat', baseUrl: 'https://router.mesthi.com/v1', apiKey: '' },
  { id: 'openai', label: 'OpenAI', name: 'OpenAI (Chat)', kind: 'openai-chat', baseUrl: 'https://api.openai.com/v1', apiKey: '' },
  { id: 'openrouter', label: 'OpenRouter', name: 'OpenRouter', kind: 'openai-chat', baseUrl: 'https://openrouter.ai/api/v1', apiKey: '' },
  { id: 'anthropic', label: 'Anthropic', name: 'Anthropic', kind: 'openai-chat', baseUrl: 'https://api.anthropic.com/v1', apiKey: '' },
  { id: 'gemini', label: 'Gemini', name: 'Google Gemini', kind: 'openai-chat', baseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai', apiKey: '' },
  { id: 'ollama', label: 'Ollama', name: 'Ollama', kind: 'openai-chat', baseUrl: 'http://localhost:11434/v1', apiKey: '' },
];

const KINDS: LlmProviderKind[] = ['openai-chat', 'openai-responses'];

function presetHint(t: Dict, id: PresetId | ''): string {
  const hints: Partial<Record<PresetId, string>> = t.settings.models.presetHint;
  return id ? (hints[id] ?? '') : '';
}

function presetName(t: Dict, preset: (typeof PRESETS)[number]): string {
  const names: Partial<Record<PresetId, string>> = t.settings.models.presetName;
  return names[preset.id] ?? preset.name;
}

/** Result of "Tes koneksi" for one provider, rendered in the current language. */
type TestResult = { state: 'testing' } | { state: 'ok'; models: string[] } | { state: 'error'; error: string | null };

interface Draft {
  id: string;
  name: string;
  kind: LlmProviderKind;
  enabled: boolean;
  baseUrl: string;
  apiKey: string;
  headers: string;
  maxTokensParam: '' | 'max_tokens' | 'max_completion_tokens';
  vision: boolean;
  models: string;
  isNew: boolean;
  /** The preset picked for a new provider (its hint is shown). */
  preset: PresetId | '';
}

/** One model per line: "id; max=16384; reasoning; effort=high; price=0.5/0.05/2; label=Nama" */
export function modelsToLines(models: LlmModelConfig[]): string {
  return models
    .map((m) =>
      [
        m.id,
        m.label ? `label=${m.label}` : '',
        m.maxOutputTokens ? `max=${m.maxOutputTokens}` : '',
        m.reasoning === true ? 'reasoning' : m.reasoning === false ? 'no-reasoning' : '',
        m.effort ? `effort=${m.effort}` : '',
        m.imageGeneration === true ? 'image-generate' : m.imageGeneration === false ? 'no-image-generate' : '',
        m.imageEditing === true ? 'image-edit' : m.imageEditing === false ? 'no-image-edit' : '',
        m.price ? `price=${m.price.input}/${m.price.cachedInput}/${m.price.output}` : '',
      ]
        .filter(Boolean)
        .join('; '),
    )
    .join('\n');
}

export function linesToModels(text: string): LlmModelConfig[] {
  const out: LlmModelConfig[] = [];
  for (const line of text.split('\n')) {
    const [id, ...opts] = line.split(';').map((s) => s.trim());
    if (!id) continue;
    const m: LlmModelConfig = { id };
    for (const o of opts) {
      const [k, v = ''] = o.split('=').map((s) => s.trim());
      if (k === 'label' && v) m.label = v;
      else if (k === 'max' && Number(v) > 0) m.maxOutputTokens = Math.round(Number(v));
      else if (k === 'reasoning') m.reasoning = true;
      else if (k === 'no-reasoning') m.reasoning = false;
      else if (k === 'effort' && v) m.effort = v;
      else if (k === 'image-generate') m.imageGeneration = true;
      else if (k === 'no-image-generate') m.imageGeneration = false;
      else if (k === 'image-edit') m.imageEditing = true;
      else if (k === 'no-image-edit') m.imageEditing = false;
      else if (k === 'price') {
        const [input, cachedInput, output] = v.split('/').map(Number);
        if ([input, cachedInput, output].every((n) => Number.isFinite(n) && n! >= 0)) m.price = { input: input!, cachedInput: cachedInput!, output: output! };
      }
    }
    out.push(m);
  }
  return out;
}

const toLines = (rec?: Record<string, string>) =>
  Object.entries(rec ?? {})
    .map(([k, v]) => `${k}=${v}`)
    .join('\n');

function fromLines(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const i = line.indexOf('=');
    if (i > 0) out[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  }
  return out;
}

function toDraft(p?: LlmProviderView): Draft {
  return {
    id: p?.id ?? '',
    name: p?.name ?? '',
    kind: p?.kind ?? 'openai-chat',
    enabled: p?.enabled ?? true,
    baseUrl: p?.baseUrl ?? '',
    apiKey: p?.apiKey ?? '',
    headers: toLines(p?.headers),
    maxTokensParam: p?.maxTokensParam ?? '',
    vision: p?.vision ?? true,
    models: modelsToLines(p?.models ?? []),
    isNew: !p,
    preset: '',
  };
}

export function ModelsPanel() {
  const t = useT();
  const words = t.settings.models;
  const [view, setView] = useState<LlmSettingsView | null>(null);
  const [route, setRoute] = useState<string[]>([]);
  const [routeDirty, setRouteDirty] = useState(false);
  const [imageModel, setImageModel] = useState('');
  const [newEntry, setNewEntry] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [tests, setTests] = useState<Record<string, TestResult>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const { reloadConfig } = useSolar();

  const apply = useCallback(
    (v: LlmSettingsView) => {
      setView(v);
      setRoute(v.routes.default ?? []);
      setRouteDirty(false);
      setImageModel(v.routes.image?.[0] ?? '');
      // the status bubble and System tab show whether the primary model is ready
      void reloadConfig();
    },
    [reloadConfig],
  );

  const load = useCallback(async () => {
    try {
      apply(await api.llm());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (op: () => Promise<LlmSettingsView | void>) => {
    setBusy(true);
    setError('');
    try {
      const v = await op();
      if (v) apply(v);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const choices = useMemo(() => {
    const out: string[] = [];
    for (const p of view?.providers ?? []) {
      if (!p.enabled) continue;
      for (const m of p.models) out.push(`${p.id}/${m.id}`);
      const test = tests[p.id];
      if (test?.state === 'ok') for (const m of test.models) out.push(`${p.id}/${m}`);
    }
    return [...new Set(out)].filter((c) => !route.includes(c));
  }, [view, tests, route]);

  const move = (i: number, d: number) => {
    const next = [...route];
    const [x] = next.splice(i, 1);
    next.splice(i + d, 0, x!);
    setRoute(next);
    setRouteDirty(true);
  };

  const save = async () => {
    if (!draft) return;
    const input: Partial<LlmProviderConfig> = {
      name: draft.name.trim(),
      kind: draft.kind,
      enabled: draft.enabled,
      baseUrl: draft.baseUrl.trim() || undefined,
      apiKey: draft.apiKey,
      headers: fromLines(draft.headers),
      maxTokensParam: draft.maxTokensParam || undefined,
      vision: draft.vision,
      models: linesToModels(draft.models),
    };
    const ok = await run(() => (draft.isNew ? api.createProvider({ ...input, id: draft.id.trim() || undefined }) : api.updateProvider(draft.id, input)));
    if (ok) setDraft(null);
  };

  const test = async (id: string) => {
    setTests((all) => ({ ...all, [id]: { state: 'testing' } }));
    try {
      const r = await api.testProvider(id);
      setTests((all) => ({ ...all, [id]: r.ok ? { state: 'ok', models: r.models ?? [] } : { state: 'error', error: r.error ?? null } }));
    } catch (e) {
      setTests((all) => ({ ...all, [id]: { state: 'error', error: e instanceof Error ? e.message : String(e) } }));
    }
  };

  const testText = (r: TestResult): string =>
    r.state === 'testing' ? words.testing : r.state === 'ok' ? words.testOk(r.models.length) : (r.error ?? t.common.failed);

  return (
    <div>
      <p className="hint" style={{ marginTop: 0 }}>
        {rich(words.intro)}
      </p>
      {error && (
        <div className="error-box" style={{ marginBottom: 10 }}>
          {error}
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h4>{words.route.title}</h4>
        </div>
        {route.map((entry, i) => (
          <div className="row" key={entry} style={{ marginTop: 8 }}>
            <span className="badge">{i === 0 ? words.route.primary : words.route.fallback(i)}</span>
            <code style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{entry}</code>
            <button type="button" className="btn small ghost" disabled={i === 0} onClick={() => move(i, -1)} aria-label={words.route.moveUp}>
              ↑
            </button>
            <button type="button" className="btn small ghost" disabled={i === route.length - 1} onClick={() => move(i, 1)} aria-label={words.route.moveDown}>
              ↓
            </button>
            <button
              type="button"
              className="btn small ghost"
              disabled={route.length === 1}
              onClick={() => {
                setRoute(route.filter((_, j) => j !== i));
                setRouteDirty(true);
              }}
              aria-label={words.route.remove}
            >
              <TrashIcon size={14} />
            </button>
          </div>
        ))}
        <div className="row" style={{ marginTop: 10 }}>
          <input
            className="input"
            style={{ flex: 1, minWidth: 180 }}
            list="solar-model-choices"
            placeholder={words.route.entryPlaceholder}
            value={newEntry}
            onChange={(e) => setNewEntry(e.target.value)}
          />
          <datalist id="solar-model-choices">
            {choices.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          <button
            type="button"
            className="btn small"
            disabled={!/^[a-z0-9][a-z0-9-]*\/.+/.test(newEntry.trim()) || route.includes(newEntry.trim())}
            onClick={() => {
              setRoute([...route, newEntry.trim()]);
              setNewEntry('');
              setRouteDirty(true);
            }}
          >
            <PlusIcon size={14} /> {t.common.add}
          </button>
        </div>
        <div className="row" style={{ marginTop: 10 }}>
          <button type="button" className="btn primary small" disabled={busy || !routeDirty || !route.length} onClick={() => void run(() => api.setRoutes({ ...view?.routes, default: route }))}>
            {words.route.save}
          </button>
          <button
            type="button"
            className="btn small"
            disabled={busy}
            onClick={() => {
              const rest = Object.fromEntries(Object.entries(view?.routes ?? {}).filter(([k]) => k !== 'default'));
              void run(() => api.setRoutes(rest));
            }}
          >
            {words.route.resetToEnv}
          </button>
        </div>
        <p className="hint">{words.route.appliesHint}</p>
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <div className="card-head"><h4>{words.imageRoutes.title}</h4></div>
        <p className="hint">{words.imageRoutes.hint}</p>
        <label className="field">
          <span>{words.imageRoutes.model}</span>
          <select className="select" value={imageModel} onChange={(e) => setImageModel(e.target.value)}>
            <option value="">{words.imageRoutes.auto}</option>
            {view?.providers.filter((p) => p.enabled).flatMap((p) => p.models
              .filter((m) => m.imageGeneration === true || m.imageEditing === true)
              .map((m) => `${p.id}/${m.id}`)).map((entry) => (
                <option key={entry} value={entry}>{entry}</option>
              ))}
            {imageModel && !view?.providers.some((p) => p.models.some((m) => `${p.id}/${m.id}` === imageModel && (m.imageGeneration || m.imageEditing))) && (
              <option value={imageModel}>{imageModel}</option>
            )}
          </select>
        </label>
        {imageModel && (() => {
          const slash = imageModel.indexOf('/');
          const provider = view?.providers.find((p) => p.id === imageModel.slice(0, slash));
          const model = provider?.models.find((m) => m.id === imageModel.slice(slash + 1));
          return (
            <p className="hint">
              {words.imageRoutes.generate}: {model?.imageGeneration ? words.imageRoutes.supported : words.imageRoutes.unsupported} ·{' '}
              {words.imageRoutes.edit}: {model?.imageEditing ? words.imageRoutes.supported : words.imageRoutes.unsupported}
            </p>
          );
        })()}
        {!view?.routes.image && (view?.routes['image-generation']?.length || view?.routes['image-edit']?.length) ? (
          <p className="hint">{words.imageRoutes.legacy}</p>
        ) : null}
        <div className="row" style={{ marginTop: 10 }}>
          <button
            type="button"
            className="btn primary small"
            disabled={busy || !imageModel}
            onClick={() => {
              const routes = Object.fromEntries(Object.entries(view?.routes ?? {})
                .filter(([key]) => !['image', 'image-generation', 'image-edit'].includes(key)));
              void run(() => api.setRoutes({ ...routes, image: [imageModel] }));
            }}
          >{words.imageRoutes.save}</button>
          <button
            type="button"
            className="btn small"
            disabled={busy}
            onClick={() => void run(() => api.setRoutes(Object.fromEntries(
              Object.entries(view?.routes ?? {}).filter(([key]) => !['image', 'image-generation', 'image-edit'].includes(key)),
            )))}
          >{words.imageRoutes.reset}</button>
        </div>
      </div>

      <div className="row" style={{ margin: '14px 0 8px' }}>
        <h4 style={{ margin: 0, flex: 1 }}>{words.providers}</h4>
        <button type="button" className="btn primary small" onClick={() => setDraft(toDraft())}>
          <PlusIcon size={16} /> {words.addProvider}
        </button>
      </div>

      {draft && (
        <div className="card">
          <h4 style={{ marginTop: 0 }}>{draft.isNew ? words.newProvider : words.editProvider(draft.id)}</h4>
          {draft.isNew && (
            <div className="row" style={{ marginBottom: 10 }}>
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  className="btn small"
                  onClick={() => setDraft({ ...draft, name: presetName(t, p), kind: p.kind, baseUrl: p.baseUrl, apiKey: p.apiKey, preset: p.id })}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
          {presetHint(t, draft.preset) && <p className="hint">{presetHint(t, draft.preset)}</p>}
          <label className="field">
            <span>{t.common.name}</span>
            <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </label>
          {draft.isNew && (
            <label className="field">
              <span>{words.fields.id}</span>
              <input className="input" value={draft.id} onChange={(e) => setDraft({ ...draft, id: e.target.value.toLowerCase() })} />
            </label>
          )}
          <label className="field">
            <span>{words.fields.kind}</span>
            <select className="select" value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as LlmProviderKind })}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {words.kind[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{words.fields.baseUrl}</span>
            <input className="input" value={draft.baseUrl} onChange={(e) => setDraft({ ...draft, baseUrl: e.target.value })} placeholder="https://host/v1" />
          </label>
          <label className="field">
            <span>{words.fields.apiKey}</span>
            <input
              className="input"
              type="password"
              autoComplete="off"
              value={draft.apiKey}
              onFocus={() => draft.apiKey === SECRET_MASK && setDraft({ ...draft, apiKey: '' })}
              onChange={(e) => setDraft({ ...draft, apiKey: e.target.value })}
            />
          </label>
          <label className="field">
            <span>{words.fields.models}</span>
            <textarea
              className="textarea"
              rows={4}
              value={draft.models}
              onChange={(e) => setDraft({ ...draft, models: e.target.value })}
              placeholder={'claude-sonnet; max=64000\nqwen3:32b; price=0/0/0'}
            />
          </label>
          {linesToModels(draft.models).length > 0 && (
            <div className="field">
              <span>{words.imageRoutes.capabilities}</span>
              <p className="hint">{words.imageRoutes.capabilityHint}</p>
              {linesToModels(draft.models).map((model, index) => (
                <div className="row" key={`${model.id}-${index}`} style={{ marginTop: 6, flexWrap: 'wrap' }}>
                  <code style={{ flex: 1, minWidth: 120, overflowWrap: 'anywhere' }}>{model.id}</code>
                  {(['imageGeneration', 'imageEditing'] as const).map((capability) => (
                    <label className="toggle" key={capability}>
                      <input
                        type="checkbox"
                        checked={model[capability] === true}
                        onChange={(e) => {
                          const models = linesToModels(draft.models);
                          models[index] = { ...models[index]!, [capability]: e.target.checked };
                          setDraft({ ...draft, models: modelsToLines(models) });
                        }}
                      />
                      {capability === 'imageGeneration' ? words.imageRoutes.generate : words.imageRoutes.edit}
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
          <details>
            <summary className="hint" style={{ cursor: 'pointer' }}>
              {words.fields.advanced}
            </summary>
            <label className="field">
              <span>{words.fields.headers}</span>
              <textarea className="textarea" rows={2} value={draft.headers} onChange={(e) => setDraft({ ...draft, headers: e.target.value })} />
            </label>
            {draft.kind === 'openai-chat' && (
              <label className="field">
                <span>{words.fields.maxTokensParam}</span>
                <select className="select" value={draft.maxTokensParam} onChange={(e) => setDraft({ ...draft, maxTokensParam: e.target.value as Draft['maxTokensParam'] })}>
                  <option value="">{words.fields.maxTokensAuto}</option>
                  <option value="max_tokens">max_tokens</option>
                  <option value="max_completion_tokens">max_completion_tokens</option>
                </select>
              </label>
            )}
            <label className="toggle" style={{ display: 'flex', marginBottom: 8 }}>
              <input type="checkbox" checked={draft.vision} onChange={(e) => setDraft({ ...draft, vision: e.target.checked })} />
              {words.fields.vision}
            </label>
          </details>
          <label className="toggle" style={{ display: 'flex', margin: '8px 0' }}>
            <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
            {t.common.active}
          </label>
          <div className="row">
            <button type="button" className="btn primary small" disabled={busy || !draft.name.trim()} onClick={() => void save()}>
              {t.common.save}
            </button>
            <button type="button" className="btn small" onClick={() => setDraft(null)}>
              {t.common.cancel}
            </button>
          </div>
        </div>
      )}

      {view?.providers.map((p) => {
        const result = tests[p.id];
        return (
          <div className="card" key={p.id}>
            <div className="card-head">
              <h4>{p.name}</h4>
              <span className="badge">{p.source === 'env' ? words.fromEnv : p.enabled ? t.common.active : t.common.inactive}</span>
              <span className="badge">{p.ready ? words.ready : words.missingKey}</span>
            </div>
            <p>
              <code>{p.id}</code> · {words.kind[p.kind]}
              {p.baseUrl ? ` · ${p.baseUrl}` : ''}
            </p>
            {p.models.length > 0 && <p className="hint">{words.modelList(p.models.map((m) => m.id).join(', '))}</p>}
            {p.missingVars.length > 0 && <p className="hint">{words.missingVars(p.missingVars.join(', '))}</p>}
            {p.source === 'env' && <p className="hint">{words.envHint}</p>}
            {result && (
              <p className="hint" style={{ color: result.state === 'error' ? 'var(--danger, #c0392b)' : undefined }}>
                {testText(result)}
              </p>
            )}
            <div className="row" style={{ marginTop: 8 }}>
              <button type="button" className="btn small" onClick={() => void test(p.id)}>
                <RefreshIcon size={14} /> {words.testConnection}
              </button>
              {p.source === 'user' && (
                <>
                  <button type="button" className="btn small" onClick={() => setDraft(toDraft(p))}>
                    {t.common.edit}
                  </button>
                  <button
                    type="button"
                    className="btn small danger"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm(words.confirmDelete(p.name))) void run(() => api.deleteProvider(p.id));
                    }}
                  >
                    {t.common.delete}
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
